import { useEffect, useState } from "react";
import posthog from "posthog-js";

import { getUserID } from "~/visits/utils/visitCookies.js";

import {
  EXPERIMENT_CHECKOUT_ARRIVAL_DATE,
  VARIANT_CONTROL,
  VARIANT_TEST,
} from "../experimentKeys";
import { useExperimentVariant } from "../useExperimentVariant";

export const ARRIVAL_DATE_EXPERIMENT = EXPERIMENT_CHECKOUT_ARRIVAL_DATE;

// How long a checkout waits for PostHog before it stops waiting and renders the
// control experience. posthog-js gives the flag request itself 3000 ms
// (feature_flag_request_timeout_ms) before giving up, so this MUST be longer than
// that: if we settled first, a slow-but-successful flag load would render control
// while PostHog records a test exposure, and the experiment would be measuring an
// experience the patient never saw.
const FLAG_FALLBACK_MS = 4000;

// One decision per patient per checkout session, kept for the life of the page.
// See useArrivalDateExperiment for why it is never revised once made.
const decisions = new Map();

// useArrivalDateExperiment decides whether this checkout renders one dated arrival
// badge per carrier -- "Estimated arrival: Mon, Oct 12" on FedEx Priority and
// "Estimated arrival: Thu, Oct 15" on USPS Standard -- in place of the generic
// shipping ranges ("2-3 days" / "5-9 days"). The server picks each carrier's
// single date (optimistic for FedEx, typical for USPS); the client only formats
// it. This hook is the only place that answers the product question: the
// checkout screens read `enabled` and nothing else.
//
// ELIGIBILITY IS THE CALLER'S. Only a mail-order checkout with a selectable shipping
// method can show an arrival date, and an edit-pending session must not switch
// presentation mid-edit. Those rules depend on what is in the cart and the session,
// which PostHog cannot see, so the caller passes `eligible` and an ineligible
// checkout never asks for the flag -- and so never records an exposure.
//
// EXPOSURE IS NATIVE. The experiment is read entirely from PostHog's own events:
// useExperimentVariant records $feature_flag_called when it resolves the flag, and
// the experiment's funnel runs from that exposure through the server-sent Visit
// Checkout and Purchase events. Nothing here reports to our API. orderType,
// consultationType and isRefill ride along on the exposure as session properties
// so results can be read per product and per initial/refill checkout.
//
// THE DECISION IS FROZEN. Flags load asynchronously, and this checkout is a form the
// patient starts editing as soon as it renders. Until the flag settles, the form
// shows a spinner in each arrival slot and settles into its arm exactly once
// (orderDetails.jsx). After that the arm must not change: a later flag answer that
// swapped the shipping copy under a half-typed address would show the patient one
// experience and measure another. So the first settled value for a (user, checkout
// session) is kept in a module-level map, returning to order details in the same
// session reads it back, and a flag answer that arrives after the fallback timer
// is ignored.
//
// Returns { ready, enabled }. The shared checkout form can render while waiting,
// with spinners in the arrival slots; date/generic-range copy and estimate
// requests wait for `ready`. `enabled` is true only for an eligible checkout
// frozen on the test variant. Holdout and unknown values read as control.
export function useArrivalDateExperiment({
  eligible,
  checkoutSessionID,
  orderType,
  consultationType,
  isRefill,
}) {
  const userID = getUserID();
  const active = Boolean(eligible && userID);
  const key = `${userID}:${checkoutSessionID}`;

  // Flags are evaluated per person, and the server-sent funnel events carry the
  // user id as distinct_id, so the exposure has to be recorded against the same
  // identity or the funnel cannot join exposure to purchase.
  useEffect(() => {
    if (active) posthog.identify(userID);
  }, [active, userID]);

  const { ready: flagsReady, variant } = useExperimentVariant(
    ARRIVAL_DATE_EXPERIMENT,
    {
      eligible: active,
      exposureProperties: eligible
        ? {
            consultation_type: consultationType,
            order_type: orderType,
            is_refill: Boolean(isRefill),
          }
        : undefined,
    },
  );

  const [decision, setDecision] = useState(() => decisions.get(key));

  useEffect(() => {
    if (!active) return undefined;
    if (decisions.has(key)) {
      setDecision(decisions.get(key));
      return undefined;
    }

    let canceled = false;
    const settle = (settledVariant) => {
      if (canceled || decisions.has(key)) return;
      const frozen = { key, variant: settledVariant };
      decisions.set(key, frozen);
      setDecision(frozen);
    };

    if (flagsReady) {
      settle(variant);
      return undefined;
    }

    // Estimates are advisory; a slow flag service must not hold up checkout.
    const timer = setTimeout(() => settle(VARIANT_CONTROL), FLAG_FALLBACK_MS);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [active, key, flagsReady, variant]);

  const frozen = decision?.key === key ? decision : undefined;
  return {
    ready: !active || Boolean(frozen),
    enabled: Boolean(active && frozen?.variant === VARIANT_TEST),
  };
}
